import { z } from "zod";

const RULE_LIMIT = 1_000;
const Pattern = z.string().trim().min(1).max(500);
export const HelpdeskCloudInfrastructureGroupRule = z.object({ pattern: Pattern, match: z.enum(["exact", "wildcard"]) }).strict();
export type HelpdeskCloudInfrastructureGroupRule = z.infer<typeof HelpdeskCloudInfrastructureGroupRule>;
export const HelpdeskCloudInfrastructureRecipientRule = HelpdeskCloudInfrastructureGroupRule.extend({ inherit: z.boolean() }).superRefine((rule, context) => {
  if (rule.inherit && !/-l[0-3]$/i.test(rule.pattern)) context.addIssue({ code: z.ZodIssueCode.custom, path: ["inherit"], message: "Inheritance requires a pattern ending in -l0 through -l3" });
});
export type HelpdeskCloudInfrastructureRecipientRule = z.infer<typeof HelpdeskCloudInfrastructureRecipientRule>;
export const HelpdeskCloudInfrastructureRecipientRules = z
  .object({
    include: z.array(HelpdeskCloudInfrastructureRecipientRule).max(RULE_LIMIT),
    exclude: z.array(HelpdeskCloudInfrastructureGroupRule).max(RULE_LIMIT),
  })
  .strict();
export type HelpdeskCloudInfrastructureRecipientRules = z.infer<typeof HelpdeskCloudInfrastructureRecipientRules>;

export function migrateHelpdeskCloudInfrastructureRecipientRules(names: readonly string[]): HelpdeskCloudInfrastructureRecipientRules {
  return { include: names.map((pattern) => ({ pattern, match: "exact", inherit: /-l[0-3]$/.test(pattern) })), exclude: [] };
}

export const HELPDESK_CLOUD_INFRASTRUCTURE_ROLE_TEMPLATE_TOKENS = [
  "resourceName",
  "accountName",
  "containerName",
  "vaultName",
  "project",
  "environment",
  "accessLevel",
  "region",
  "vmBase",
  "schema",
] as const;

const GroupNameTemplate = z
  .string()
  .trim()
  .min(1)
  .max(500)
  .superRefine((template, context) => {
    const tokens: readonly string[] = HELPDESK_CLOUD_INFRASTRUCTURE_ROLE_TEMPLATE_TOKENS;
    const remaining = template.replace(/\{([^{}]+)\}/g, (_match: string, token: string) => {
      if (!tokens.includes(token)) context.addIssue({ code: z.ZodIssueCode.custom, message: `Unknown role template token: ${token}` });
      return "";
    });
    if (/[{}]/.test(remaining)) context.addIssue({ code: z.ZodIssueCode.custom, message: "Unbalanced role template braces" });
    if (!template.includes("{accessLevel}")) context.addIssue({ code: z.ZodIssueCode.custom, message: "Role templates must include {accessLevel}" });
  });

const RESOURCE_SUBTYPES: Record<string, readonly string[]> = {
  storageaccounts: ["", "fileshare"],
  storageaccountcontainer: [""],
  servers: [""],
  databases: ["", "schema"],
  vaults: ["secret", "key"],
  virtualmachines: [""],
};

export const HelpdeskCloudInfrastructureRoleTemplate = z
  .object({
    id: z.string().trim().min(1).max(100),
    label: z.string().trim().min(1).max(200),
    resourceType: z.enum(["storageaccounts", "storageaccountcontainer", "servers", "databases", "vaults", "virtualmachines"]),
    resourceSubtype: z.string().trim().max(128),
    subscriptions: z.array(z.string().trim().min(1).max(200)).max(1_000).optional(),
    resourceNamePattern: Pattern.optional(),
    accessLevels: z
      .array(
        z
          .string()
          .trim()
          .min(1)
          .max(100)
          .regex(/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/),
      )
      .min(1)
      .max(100)
      .refine((levels) => new Set(levels).size === levels.length, "Access codes must be unique"),
    groupNameTemplate: GroupNameTemplate,
    nameTransform: z.enum(["none", "sql", "virtualMachine"]).optional(),
  })
  .strict()
  .superRefine((template, context) => {
    if (!RESOURCE_SUBTYPES[template.resourceType]?.includes(template.resourceSubtype)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["resourceSubtype"], message: "Access scope is not supported by this resource type" });
    }
    if ((template.nameTransform === "sql" && template.resourceType !== "databases") || (template.nameTransform === "virtualMachine" && template.resourceType !== "virtualmachines")) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["nameTransform"], message: "Name transformation is not supported by this resource type" });
    }
    const isSchemaScope = template.resourceType === "databases" && template.resourceSubtype === "schema";
    if (isSchemaScope && template.nameTransform !== "sql") {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["nameTransform"], message: "MSSQL schema access requires the SQL convention name transformation" });
    }
    if (isSchemaScope && !template.groupNameTemplate.includes("{schema}")) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["groupNameTemplate"], message: "Role templates for MSSQL schema access must include {schema}" });
    }
    const allowedTokens: Record<string, boolean> = {
      accountName: template.resourceType === "storageaccounts" || template.resourceType === "storageaccountcontainer",
      containerName: template.resourceType === "storageaccountcontainer",
      vaultName: template.resourceType === "vaults",
      region: template.resourceType === "virtualmachines" && template.nameTransform === "virtualMachine",
      vmBase: template.resourceType === "virtualmachines" && template.nameTransform === "virtualMachine",
      schema: isSchemaScope && template.nameTransform === "sql",
    };
    for (const [token, allowed] of Object.entries(allowedTokens)) {
      if (!allowed && template.groupNameTemplate.includes(`{${token}}`)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["groupNameTemplate"], message: `Token {${token}} is not available for this resource type, scope, and transformation` });
      }
    }
  });
export type HelpdeskCloudInfrastructureRoleTemplate = z.infer<typeof HelpdeskCloudInfrastructureRoleTemplate>;
export const HelpdeskCloudInfrastructureRoleTemplates = z
  .array(HelpdeskCloudInfrastructureRoleTemplate)
  .max(1_000)
  .refine((templates) => new Set(templates.map(({ id }) => id)).size === templates.length, "Role template IDs must be unique");
export type HelpdeskCloudInfrastructureRoleTemplates = z.infer<typeof HelpdeskCloudInfrastructureRoleTemplates>;

export function defaultHelpdeskCloudInfrastructureRoleTemplates(): HelpdeskCloudInfrastructureRoleTemplates {
  return [
    {
      id: "storage-blob",
      label: "Storage account Blob access",
      resourceType: "storageaccounts",
      resourceSubtype: "",
      accessLevels: ["ro", "rwd"],
      groupNameTemplate: "role-azsa-blob-{accountName}-{accessLevel}",
    },
    {
      id: "storage-files",
      label: "Storage account file shares",
      resourceType: "storageaccounts",
      resourceSubtype: "fileshare",
      accessLevels: ["ro", "rwd"],
      groupNameTemplate: "role-azsa-fileshare-{accountName}-{accessLevel}",
    },
    {
      id: "storage-container",
      label: "Blob container access",
      resourceType: "storageaccountcontainer",
      resourceSubtype: "",
      accessLevels: ["ro", "rwd"],
      groupNameTemplate: "role-azsa-blob-{accountName}-{containerName}-{accessLevel}",
    },
    { id: "sql-server", label: "SQL server access", resourceType: "servers", resourceSubtype: "", accessLevels: ["admin"], groupNameTemplate: "role-sql-{resourceName}-{accessLevel}" },
    {
      id: "sql-database",
      label: "SQL database access",
      resourceType: "databases",
      resourceSubtype: "",
      accessLevels: ["rwdml", "rwddl", "sysadmin", "ro"],
      groupNameTemplate: "role-mssql-{environment}-{resourceName}-{accessLevel}",
      nameTransform: "sql",
    },
    {
      id: "sql-schema",
      label: "SQL database schema access",
      resourceType: "databases",
      resourceSubtype: "schema",
      accessLevels: ["rwdml", "rwddl", "sysadmin", "ro"],
      groupNameTemplate: "role-mssql-{environment}-{resourceName}-{schema}-{accessLevel}",
      nameTransform: "sql",
    },
    {
      id: "vault-secret",
      label: "Key Vault secrets",
      resourceType: "vaults",
      resourceSubtype: "secret",
      accessLevels: ["ro", "rw", "rwd"],
      groupNameTemplate: "role-azkv-secret-{vaultName}-{accessLevel}",
    },
    { id: "vault-key", label: "Key Vault keys", resourceType: "vaults", resourceSubtype: "key", accessLevels: ["ro", "rw", "rwd"], groupNameTemplate: "role-azkv-key-{vaultName}-{accessLevel}" },
    {
      id: "virtual-machine",
      label: "Virtual machine access",
      resourceType: "virtualmachines",
      resourceSubtype: "",
      accessLevels: ["contributor", "admin"],
      groupNameTemplate: "role-vm-{project}-{environment}-{region}-{vmBase}-{accessLevel}",
      nameTransform: "virtualMachine",
    },
  ];
}
